"use client";

import { useGetRatingOptionsQuery, useGetCallRatingQuery, useCheckRatingStatusQuery, useSubmitRatingMutation } from "../lib/api/apiSlice";
import { fetcher } from "../lib/fetcher";

// Types for rating options
export interface IssueOption {
  id: string;
  label: string;
  description?: string;
  category?: string;
}

export interface IssueCategory {
  id: string;
  label: string;
  issues: IssueOption[];
}

export interface RatingOptions {
  hierarchical: IssueCategory[];
  flat: IssueOption[];
}

// Types for call rating
export interface CallRating {
  _id: string;
  user_id: string;
  conversation_id: string;
  rating: number;
  issues?: string[];
  feedback?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateRatingRequest {
  conversation_id: string;
  rating: number;
  issues?: string[];
  feedback?: string;
}

export interface UpdateRatingRequest {
  issues?: string[];
  feedback?: string;
}

// Combined hook for rating options, submit, and update
export const useRatingActions = () => {
  const { data, error, isLoading } = useGetRatingOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const [submitRatingMutation] = useSubmitRatingMutation();

  const submitRating = async (request: CreateRatingRequest) => {
    const response = await submitRatingMutation(request).unwrap();
    return response.data;
  };

  const updateRating = async (conversationId: string, request: UpdateRatingRequest) => {
    const response = await fetcher<{ success: boolean; data: { message: string } }>(
      `/protected/rating/update`,
      {
        method: "POST",
        body: JSON.stringify({
          conversation_id: conversationId,
          ...request,
        }),
        requireAuth: true,
      }
    );
    return response.data;
  };

  return {
    options: data?.data,
    loading: isLoading,
    error,
    submitRating,
    updateRating,
  };
};

// Hook to fetch rating for a specific conversation
export const useCallRating = (conversationId?: string) => {
  const { data, error, isLoading, refetch } = useGetCallRatingQuery(
    { conversationId: conversationId! },
    { skip: !conversationId }
  );

  return {
    rating: data?.data,
    loading: isLoading,
    error,
    mutate: refetch,
  };
};


// Hook to check if user should see rating prompt (fallback when no localStorage)
export const useRatingStatus = (shouldCheck: boolean) => {
  const { data, error, isLoading } = useCheckRatingStatusQuery(undefined, {
    skip: !shouldCheck,
  });

  return {
    shouldShowRating: data?.data?.should_show_rating || false,
    conversationId: data?.data?.conversation_id || "",
    loading: isLoading,
    error,
  };
};
